import { DisplayCity, Homes_GET_RESPONSE } from '@sales-app/api/DisplayCity/DisplayCity'
import { GeoLocation } from '@sales-app/api/GeoLocation/GeoLocation'
import { transformHomeFilterToHomeAPIQuery } from '@sales-app/context/workspace/homeFilters.util'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { keepPreviousData, QueryObserverResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useState } from 'react'

import { useAPI } from '@/hooks'

import { appendGeoHomesDataToMarketData, appendMarketPageData, DisplayCityData } from './useRecommendedHomes.util'

export function useRecommendedHomes() {
  const [{ tabs, activeTabId, activeTabType }] = useWorkspaceContext()
  const activeTab = tabs.find((t) => t.bookablePersonId === activeTabId && t.bookablePersonType === activeTabType)
  const filters = activeTab?.potentialBookingFilters || { listView: true }
  const { displayCity: displayCityObj } = filters
  const displayCity = displayCityObj?.slug
  const RESULTS_PER_PAGE = 32
  const [totalPages, setTotalPages] = useState(1)
  const [homePerPage, setHomePerPage] = useState(1)
  const [geoMarketData, setGeoMarketData] = useState<Homes_GET_RESPONSE>()
  const [isWithinZoomBounds, setIsWithinZoomBounds] = useState(true)

  const { neLatitude, neLongitude, swLatitude, swLongitude, ...searchQueryWithoutGeoLocation } = filters
  const isGeoLocationQuery = !!swLatitude || !!swLongitude || !!neLatitude || !!neLongitude

  const displayCityAPIQueryParams = transformHomeFilterToHomeAPIQuery(searchQueryWithoutGeoLocation)
  const geoHomesAPIQueryParams = transformHomeFilterToHomeAPIQuery(filters)

  const currentPage = filters.currentPage ? Number(filters.currentPage) : 1
  const hasNextPage = totalPages > currentPage

  const { reservationType } = searchQueryWithoutGeoLocation
  const { end_date } = displayCityAPIQueryParams

  const displayCityQuery = useAPI<DisplayCity.GET_HOMES.Response, AxiosError>(DisplayCity.getHomes(displayCity), {
    params: {
      ...displayCityAPIQueryParams,
      page: currentPage,
      grouped: true,
      use_true_pagination: true,
      installed: reservationType === 'ShortTerm' ? true : undefined,
      standby: reservationType === 'Standby' ? true : undefined,
    },
    enabled: !!displayCity,
    placeholderData: keepPreviousData,
    nonKeyParams: { results_per_page: RESULTS_PER_PAGE },
  })

  const {
    data: geoHomesData,
    error: homesError,
    ...geoHomesQuery
  } = useAPI<GeoLocation.GET_GEOLOCATION_HOMES.Response, AxiosError>(GeoLocation.getGeoLocationHomes(), {
    params: {
      ...geoHomesAPIQueryParams,
      page: currentPage,
      installed: reservationType !== 'Flexible' && end_date ? true : undefined,
      standby: reservationType === 'Standby' ? true : undefined,
    },
    retry: false,
    nonKeyParams: { results_per_page: RESULTS_PER_PAGE },
    enabled: isGeoLocationQuery,
    placeholderData: keepPreviousData,
  })

  const isFetching = geoHomesQuery?.isFetching || displayCityQuery?.isFetching
  const displayCityData = displayCityQuery.data

  useEffect(() => {
    if (!displayCityData || isFetching || !isGeoLocationQuery) return
    const isWithinZoomBounds = homesError?.response?.status !== 422
    const geoMarketData = appendGeoHomesDataToMarketData({
      geoHomesData,
      isWithinZoomBounds,
      displayCityData,
    })
    setGeoMarketData(geoMarketData)
    setIsWithinZoomBounds(isWithinZoomBounds)
  }, [displayCityData, geoHomesData, homesError, isGeoLocationQuery, isFetching])

  const data = useMemo(() => {
    const geoLocationData = isGeoLocationQuery && geoMarketData
    const nonMarketData = !displayCity && geoHomesData
    const apiData = geoLocationData || nonMarketData || displayCityData

    setTotalPages(apiData?.data.total_pages || 0)
    setHomePerPage(apiData?.data.results_per_page || 0)

    if (apiData) return appendMarketPageData(apiData as any)
  }, [geoMarketData, displayCityData, isGeoLocationQuery, displayCity, geoHomesData])

  return {
    ...displayCityQuery,
    data,
    currentPage,
    isFetching,
    isWithinZoomBounds,
    homePerPage,
    hasNextPage,
  }
}

export type RecommendedHomesQuery<T> = Omit<QueryObserverResult<T>, 'data'> & {
  currentPage: number
  data?: { data: T }
  hasNextPage: boolean
  homePerPage: number
  isWithinZoomBounds: boolean
  totalFiltersApplied?: number
  totalPages: number
}

export type UseRecommendedHomes = RecommendedHomesQuery<DisplayCityData>
